import { useState, useRef, useEffect, Dispatch } from 'react'
import { type DateRange } from '@/Views/FormSteps/DateTime/Utilities/getBriefDates'
import useLocale from '@/Hooks/Locale/useLocale'
import { CalanderIcon, ChevronLeft, ChevronRight, ChevronDown } from '@/Components/UI/Icons'

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

const pad = (n: number) => String(n).padStart(2, '0')

interface DuplicateDatePickerProps {
  dates: DateRange
  setDates: Dispatch<React.SetStateAction<DateRange>>
  hasError?: boolean
}

const DuplicateDatePicker = ({ dates, setDates, hasError }: DuplicateDatePickerProps) => {
  const locale = useLocale()
  const isUS = locale === 'US'

  const formatDate = (d: Date) =>
    isUS
      ? `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}`
      : `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`

  const singlePlaceholder = isUS ? 'YYYY/MM/DD – YYYY/MM/DD' : 'DD/MM/YYYY – DD/MM/YYYY'

  const [startDate, endDate] = dates
  const [open, setOpen] = useState(false)
  const [selectingEnd, setSelectingEnd] = useState(false)
  const [hoverDate, setHoverDate] = useState<Date | undefined>()
  const [viewDate, setViewDate] = useState(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })

  const ref = useRef<HTMLDivElement>(null)
  const today = new Date(new Date().setHours(0, 0, 0, 0))

  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
        setSelectingEnd(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  const handleDayClick = (day: Date) => {
    if (day < today) return
    if (!selectingEnd || !startDate) {
      setDates([day, undefined])
      setSelectingEnd(true)
    } else {
      if (isSameDay(day, startDate)) {
        setDates([startDate, startDate])
      } else if (day < startDate) {
        setDates([day, startDate])
      } else {
        setDates([startDate, day])
      }
      setSelectingEnd(false)
      setOpen(false)
    }
  }

  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const firstDayOffset = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const yearOptions = Array.from({ length: 10 }, (_, i) => today.getFullYear() + i)

  const previewEnd =
    selectingEnd && hoverDate && startDate && hoverDate >= startDate ? hoverDate : endDate
  const previewStart =
    selectingEnd && hoverDate && startDate && hoverDate < startDate ? hoverDate : startDate

  const inputValue = startDate
    ? endDate
      ? `${formatDate(startDate)} – ${formatDate(endDate)}`
      : `${formatDate(startDate)} – `
    : ''

  return (
    <div ref={ref} className='relative w-full'>
      <button
        type='button'
        onClick={() => setOpen((open) => !open)}
        className={`flex items-center justify-between px-3 border rounded-[4px] bg-white
          outline-none transition-colors w-full
          ${
            open
              ? 'border-[#3e3e3e] ring-2 ring-[rgba(126,126,126,0.2)]'
              : hasError
              ? 'border-[#af231c]'
              : 'border-[#bfbfbf]'
          }`}
        style={{ height: 'calc(1.5em + 18px)', fontFamily: 'Montserrat' }}>
        <span
          className={`text-sm sm:text-base leading-6 ${
            inputValue ? 'text-[#262626] font-medium' : 'text-[#bfbfbf]'
          }`}>
          {inputValue || singlePlaceholder}
        </span>
        <CalanderIcon />
      </button>

      {open && (
        <div
          className='absolute left-0 top-full mt-1 z-50 bg-white border border-[#d9d9d9] 
          rounded-[8px] shadow-md w-full p-4'>
          <div className='flex items-center justify-between mb-3'>
            <button
              type='button'
              onClick={() => setViewDate(new Date(year, month - 1, 1))}
              className='w-9 h-9 flex items-center justify-center rounded hover:bg-[#f0f0f0] transition-colors'>
              <ChevronLeft />
            </button>

            <div className='flex items-center gap-3'>
              <div className='relative flex items-center'>
                <select
                  value={month}
                  onChange={(e) => setViewDate(new Date(year, +e.target.value, 1))}
                  className='font-semibold text-[#1f1f1f] text-sm bg-transparent outline-none 
                  cursor-pointer appearance-none pr-5'>
                  {MONTHS.map((months, i) => (
                    <option key={months} value={i}>
                      {months}
                    </option>
                  ))}
                </select>
                <span className='pointer-events-none absolute right-0 top-1/2 -translate-y-1/2'>
                  <ChevronDown />
                </span>
              </div>

              <div className='relative flex items-center'>
                <select
                  value={year}
                  onChange={(e) => setViewDate(new Date(+e.target.value, month, 1))}
                  className='font-semibold text-[#1f1f1f] text-sm bg-transparent outline-none 
                  cursor-pointer appearance-none pr-5'>
                  {yearOptions.map((years) => (
                    <option key={years} value={years}>
                      {years}
                    </option>
                  ))}
                </select>
                <span className='pointer-events-none absolute right-0 top-1/2 -translate-y-1/2'>
                  <ChevronDown />
                </span>
              </div>
            </div>

            <button
              type='button'
              onClick={() => setViewDate(new Date(year, month + 1, 1))}
              className='w-9 h-9 flex items-center justify-center rounded hover:bg-[#f0f0f0] transition-colors'>
              <ChevronRight />
            </button>
          </div>

          <div className='grid grid-cols-7 mb-1'>
            {WEEKDAYS.map((days) => (
              <div key={days} className='text-center text-xs font-semibold text-[#1f1f1f] py-1'>
                {days}
              </div>
            ))}
          </div>

          <div className='grid grid-cols-7'>
            {Array.from({ length: firstDayOffset }, (_, i) => (
              <div key={`e${i}`} />
            ))}

            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = new Date(year, month, i + 1)
              const isPast = day < today
              const isStart = !!startDate && isSameDay(day, startDate)
              const isEnd = !!endDate && isSameDay(day, endDate)
              const isToday = isSameDay(day, today)
              const inRange =
                !!previewStart &&
                !!previewEnd &&
                !isSameDay(day, previewStart) &&
                !isSameDay(day, previewEnd) &&
                day > previewStart &&
                day < previewEnd

              return (
                <button
                  key={i}
                  type='button'
                  disabled={isPast}
                  onClick={() => handleDayClick(day)}
                  onMouseEnter={() => selectingEnd && setHoverDate(day)}
                  onMouseLeave={() => selectingEnd && setHoverDate(undefined)}
                  className={[
                    'h-9 w-full text-sm flex items-center justify-center transition-colors',
                    isPast ? 'text-[#d9d9d9] cursor-not-allowed' : 'cursor-pointer',
                    isStart || isEnd ? 'bg-black text-white font-semibold rounded-full' : '',
                    inRange ? 'bg-[#f0f0f0] text-[#1f1f1f]' : '',
                    !isPast && !isStart && !isEnd ? 'hover:bg-[#f0f0f0] hover:rounded-full' : '',
                    isToday && !isStart && !isEnd
                      ? 'ring-1 ring-inset ring-black rounded-full font-semibold'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}>
                  {i + 1}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default DuplicateDatePicker
